import { createSignal, onMount } from 'solid-js'
import DarkSwitch from '@lib/components/DarkSwitch'
import LanguageSwitch from '@lib/components/LanguageSwitch'
import Icon from '@lib/components/Icon'
import { res2json } from '@/utils'
import { t } from '@lib/i18n'
import loginBg from '@lib/assets/login_background.webp'
import ParticleCanvas from './ParticleCanvas'

/**
 * @param {Object} props
 * @param {string} props.appName - 顯示名稱（如 "CRM"）
 * @param {string} props.logo - Logo 圖片 import
 * @param {boolean} [props.enableTOTP=false] - 是否啟用 TOTP 二階段驗證
 * @param {string} [props.apiPrefix="/api/v1"] - API 前綴
 * @param {string} [props.brandName="CHOICE"] - 登入頁左下角品牌字（CRM/staff 用預設 CHOICE，BI 傳 ONION）
 */
export default function Login(props) {
  const apiPrefix = () => props.apiPrefix ?? '/api/v1'
  const [action, setAction] = createSignal('form')
  const [secret, setSecret] = createSignal('')
  const [url, setUrl] = createSignal('')

  return (
    <div class="fixed inset-0 flex flex-col lg:flex-row bg-base-100">
      {/* 背景圖：手機版佔上方約 44% 視高並被下方卡片疊上 35px；桌機版為左側 3/5 */}
      <div class="relative shrink-0 h-[44dvh] min-h-[260px] overflow-hidden lg:h-auto lg:min-h-0 lg:w-3/5">
        <img src={loginBg} alt="" class="absolute inset-0 w-full h-full object-cover" />
        <ParticleCanvas />
        <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" style={{ 'z-index': 2 }} />
        <div class="absolute left-8 bottom-[65px] text-white lg:left-12 lg:bottom-12" style={{ 'z-index': 3 }}>
          <h2 class="text-[46px] leading-none font-bold tracking-widest lg:text-6xl lg:leading-normal">{props.brandName ?? 'CHOICE'}</h2>
          <p class="mt-[9px] text-[10.7px] leading-[15px] tracking-[0.25em] text-white/70 uppercase lg:mt-3 lg:text-sm lg:leading-normal">Insight Beyond Data</p>
        </div>
      </div>
      {/* 語言／深淺色切換：手機版落在背景圖右上（淺字），桌機版落在右側表單區右上 */}
      <div class="absolute top-4 right-4 z-20 flex items-center gap-1 text-white/80 lg:text-base-content">
        <LanguageSwitch showLabel />
        <DarkSwitch />
      </div>
      {/* 表單卡片 */}
      <div class="relative z-10 flex-1 min-h-0 -mt-[35px] overflow-x-hidden overflow-y-auto rounded-t-[20px] bg-base-100 lg:mt-0 lg:flex-none lg:w-2/5 lg:rounded-none lg:flex lg:items-center lg:justify-center">
        {action() === 'form' && <LoginForm appName={props.appName} logo={props.logo} enableTOTP={props.enableTOTP} apiPrefix={apiPrefix()} setSecret={setSecret} setUrl={setUrl} setAction={setAction} />}
        {action() === 'confirm' && <LoginConfirm appName={props.appName} logo={props.logo} apiPrefix={apiPrefix()} secret={secret} url={url} />}
      </div>
    </div>
  )
}

function LoginForm({ appName, logo, enableTOTP, apiPrefix, setSecret, setUrl, setAction }) {
  const [username, setUsername] = createSignal('')
  const [password, setPassword] = createSignal('')
  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})
  const [eyesOpen, setEyesOpen] = createSignal(false)

  let elFocus
  onMount(() => elFocus?.focus())

  async function submit(e) {
    e.preventDefault()
    if (sending()) return

    setSending(true)
    return fetch(`${apiPrefix}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: username().trim(),
        password: password(),
      }),
    })
      .then(res2json)
      .then((r) => {
        if (r.status >= 400) {
          setErrors(r.data)
        } else if (enableTOTP) {
          setSecret(r.data.secret ?? '')
          setUrl(r.data.otp_url ?? '')
          setAction('confirm')
        } else {
          document.location.reload()
        }
      })
      .finally(() => setSending(false))
  }

  return (
    <div class="w-full px-7 pt-8 pb-[46px] space-y-8 lg:max-w-sm lg:px-8 lg:py-0">
      <div>
        <h1 class="text-2xl font-bold">
          {t('app', 'Welcome')} {appName}
        </h1>
        <p class="text-base-content/60 mt-1">{t('form', 'Enter your credentials to access the terminal.')}</p>
      </div>
      <form onSubmit={submit}>
        <fieldset class="fieldset fieldset-touch">
          <legend class="fieldset-legend uppercase">{t('app', 'Username')}</legend>
          <label class="input w-full">
            <input ref={elFocus} type="text" name="username" value={username()} onChange={(e) => setUsername(e.target.value)} class="grow" tabindex="1" required />
          </label>
          {errors().username && <p class="label text-error">{t('form', errors().username)}</p>}
        </fieldset>
        <fieldset class="fieldset fieldset-touch" style={{ 'margin-top': '1.5rem' }}>
          <legend class="fieldset-legend uppercase">{t('form', 'Password')}</legend>
          <label class="input w-full">
            <input type={eyesOpen() ? 'text' : 'password'} name="password" value={password()} onChange={(e) => setPassword(e.target.value)} class="grow" tabindex="2" required />
            <div class="tooltip flex" data-tip={t('form', 'View password')}>
              <button type="button" class="flex items-center leading-none cursor-pointer" onClick={() => setEyesOpen((e) => !e)}>
                {eyesOpen() ? <Icon name="eye-off-line" size={16} /> : <Icon name="eye-line" size={16} />}
              </button>
            </div>
          </label>
          {errors().password && <p class="label text-error">{t('form', errors().password)}</p>}
        </fieldset>
        {errors().error && <p class="label text-error">{t('form', errors().error)}</p>}
        <div style={{ 'margin-top': '1.5rem' }}>
          <button type="submit" class="btn btn-primary w-full rounded-lg h-12 shadow-[0_5px_9px_0_rgba(37,120,255,0.12)]" tabindex="4" disabled={sending()}>
            {sending() ? <span class="loading loading-dots"></span> : <>{t('form', 'Sign In')} &rarr;</>}
          </button>
        </div>
      </form>
    </div>
  )
}

function LoginConfirm({ appName, logo, apiPrefix, secret, url }) {
  const [otp, setOtp] = createSignal('')
  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})

  let elFocus, elQrcode
  onMount(() => {
    elFocus?.focus()
    if (url()) {
      elQrcode.innerHTML = ''
      new window.QRCode(elQrcode, { text: url(), width: 256, height: 256, correctLevel: window.QRCode.CorrectLevel.H })
    }
  })

  async function submit(e) {
    e.preventDefault()
    if (sending()) return

    setSending(true)
    return fetch(`${apiPrefix}/login/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ otp: otp().trim() }),
    })
      .then(res2json)
      .then((r) => {
        if (r.status >= 400) {
          setErrors(r.data)
        } else {
          document.location.reload()
        }
      })
      .finally(() => setSending(false))
  }

  return (
    <div class="w-full px-7 pt-8 pb-[46px] space-y-8 lg:max-w-sm lg:px-8 lg:py-0">
      <div>
        <h1 class="text-2xl font-bold">
          {t('app', 'Welcome')} {appName}
        </h1>
        <p class="text-base-content/60 mt-1">{t('form', 'Enter your OTP to log in')}</p>
      </div>
      <form onSubmit={submit}>
        <fieldset class="fieldset fieldset-touch">
          <legend class="fieldset-legend uppercase">{t('form', 'OTP')}</legend>
          <label class="input w-full">
            <Icon name="keyboard-box-line" />
            <input ref={elFocus} type="number" name="otp" value={otp()} onChange={(e) => setOtp(e.target.value)} class="grow no-spin" tabindex="3" required />
          </label>
          {errors().otp && <p class="label text-error">{t('form', errors().otp)}</p>}
        </fieldset>
        {errors().error && <p class="label text-error">{t('form', errors().error)}</p>}
        {url() && (
          <fieldset class="fieldset" style={{ 'margin-top': '1.5rem' }}>
            <legend class="fieldset-legend">{t('form', 'To receive your OTP, please scan the QR code using Google Authenticator or any other authenticator app')}</legend>
            <div ref={elQrcode} class="textarea textarea-bordered flex justify-center dark:bg-white"></div>
          </fieldset>
        )}
        {secret() && (
          <fieldset class="fieldset" style={{ 'margin-top': '1.5rem' }}>
            <legend class="fieldset-legend">{t('form', 'Alternatively, copy and paste this secret code into your authenticator app to receive your OTP')}</legend>
            <div class="input select-all">{secret()}</div>
          </fieldset>
        )}
        <div style={{ 'margin-top': '1.5rem' }}>
          <button type="submit" class="btn btn-primary w-full rounded-lg h-12 shadow-[0_5px_9px_0_rgba(37,120,255,0.12)]" tabindex="4" disabled={sending()}>
            {sending() ? <span class="loading loading-dots"></span> : <>{t('form', 'Sign In')} &rarr;</>}
          </button>
        </div>
      </form>
    </div>
  )
}
